import { ShadowGenerator } from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import '@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent';
import { ShadowLight } from '@babylonjs/core/Lights/shadowLight';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Scene } from '@babylonjs/core/scene';

import { getTextureUrl } from '../../utils/assetsUrlBuilder';
import ArticleNode from '../nodes/articleNode';
import Settings from '../settings/settings';

export class AutoShadowCastingLightCompound {
  readonly shadowGenerator: ShadowGenerator;
  readonly light: ShadowLight;

  constructor(light: ShadowLight) {
    this.light = light;
    this.shadowGenerator = new ShadowGenerator(Settings.SHADOWS_MAP_SIZE, light);
    this.shadowGenerator.useBlurCloseExponentialShadowMap = true;
    this.shadowGenerator.forceBackFacesOnly = false;
    this.shadowGenerator.getShadowMap().refreshRate = 0;
  }
}

/** Helper class that composes the arragement of lights around the center of the stage. */
export default class LightController {
  private lights: AutoShadowCastingLightCompound[] = [];

  constructor(scene: Scene) {
    this.createTopLight(scene);
    this.createBackLight(scene);
    this.createRightLight(scene);
    this.createLeftLight(scene);
    this.createEnvironmentLight(scene);
  }

  private createTopLight(scene: Scene) {
    const position = new Vector3(0, 370, 0);
    const direction = new Vector3(0, -1, 0);
    const light = new SpotLight('TopLight', position, direction, 2, 10, scene);
    light.intensity = 60000;
    this.lights.push(new AutoShadowCastingLightCompound(light));
  }

  private createBackLight(scene: Scene) {
    const position = new Vector3(200, 150, 0);
    const direction = new Vector3(-1, -0.65, 0);
    const light = new SpotLight('BackLight', position, direction, Math.PI, 10, scene);
    light.intensity = 30000;
    light.shadowEnabled = false;
  }

  private createLeftLight(scene: Scene) {
    const position = new Vector3(0, 130, 150);
    const direction = new Vector3(0, -0.65, -1);
    const light = new SpotLight('LeftLight', position, direction, Math.PI, 10, scene);
    light.intensity = 35000;
    light.shadowEnabled = false;
  }

  private createRightLight(scene: Scene) {
    const position = new Vector3(0, 130, -200);
    const direction = new Vector3(0, -0.65, 1);
    const light = new SpotLight('RightLight', position, direction, Math.PI, 10, scene);
    light.intensity = 35000;
    light.shadowEnabled = false;
  }

  private createEnvironmentLight(scene: Scene) {
    const environmentTexture = new CubeTexture(getTextureUrl('studio-skybox.env'), scene);
    environmentTexture.gammaSpace = false;
    // Rotate texture for less reflection on table
    environmentTexture.rotationY = 0.7; // 40.10704565915762 degress
    environmentTexture.level = 0.5;
    scene.environmentIntensity = 1.5;
    scene.environmentTexture = environmentTexture;
    scene.shadowsEnabled = true;
  }

  public addArticleToLightHierarchy(article: ArticleNode) {
    article.allMeshes.forEach(this.addMeshToLightHierarchy);
    this.lights.forEach((light) => light.shadowGenerator.recreateShadowMap());
  }

  public addMeshToLightHierarchy = (mesh: AbstractMesh) => {
    this.lights.forEach((light) => light.shadowGenerator.addShadowCaster(mesh));
  };
}
